import { FormReviewData, VaccineHistory } from "@/types";
import { formatDate } from "@/utils/formatters";

interface VaccineStatusSectionProps {
  formData: FormReviewData;
}

export function VaccineStatusSection({ formData }: VaccineStatusSectionProps) {
  const vaccines: VaccineHistory =
    formData?.formData?.vaccine_history || ({} as VaccineHistory);

  const vaccinesReceived = ((vaccines as any).vaccines_received || []).map(
    (v: string) => v.trim()
  );

  const getVaccineDate = (dateKey: string) => {
    return (vaccines as any)[dateKey];
  };

  const vaccineList = [
    {
      key: "tdap",
      name: "Tetanus (Td/Tdap)",
      type: undefined,
      date:
        getVaccineDate("tdap_date") ||
        getVaccineDate("tetanus_date") ||
        getVaccineDate("td_date"),
      dataKey: "Tetanus (Td/Tdap)",
    },
    {
      key: "mmr",
      name: "MMR",
      type: undefined,
      date: getVaccineDate("mmr_date"),
      dataKey: "MMR",
    },
    {
      key: "polio",
      name: "Polio",
      type: undefined,
      date: getVaccineDate("polio_date"),
      dataKey: "Polio",
    },
    {
      key: "hepatitis_a",
      name: "Hepatitis A",
      type: undefined,
      date: getVaccineDate("hepatitis_a_date") || getVaccineDate("hepa_date"),
      dataKey: "Hepatitis A",
    },
    {
      key: "hepatitis_b",
      name: "Hepatitis B",
      type: undefined,
      date: getVaccineDate("hepatitis_b_date") || getVaccineDate("hepb_date"),
      dataKey: "Hepatitis B",
    },
    {
      key: "influenza",
      name: "Influenza (Flu)",
      type: undefined,
      date: getVaccineDate("influenza_date"),
      dataKey: "Influenza (Flu)",
    },
    {
      key: "covid_vaccine",
      name: "COVID-19",
      type: vaccines.covid_vaccine_type,
      date:
        getVaccineDate("covid_vaccine_date") || getVaccineDate("covid19_date"),
      dataKey: "COVID-19",
    },
    {
      key: "typhoid",
      name: "Typhoid",
      type: undefined,
      date: getVaccineDate("typhoid_date"),
      dataKey: "Typhoid",
    },
    {
      key: "yellow_fever_vaccine",
      name: "Yellow Fever",
      type: undefined,
      date: getVaccineDate("yellow_fever_date"),
      dataKey: "Yellow Fever",
    },
    {
      key: "rabies",
      name: "Rabies",
      type: undefined,
      date: getVaccineDate("rabies_date"),
      dataKey: "Rabies",
    },
    {
      key: "meningococcal",
      name: "Meningococcal",
      type: undefined,
      date: getVaccineDate("meningococcal_date"),
      dataKey: "Meningococcal",
    },
    {
      key: "japanese_encephalitis",
      name: "Japanese Encephalitis",
      type: undefined,
      date:
        getVaccineDate("je_date") ||
        getVaccineDate("japanese_encephalitis_date"),
      dataKey: "Japanese Encephalitis",
    },
    {
      key: "hpv",
      name: "HPV",
      type: undefined,
      date: getVaccineDate("hpv_date"),
      dataKey: "HPV",
    },
    {
      key: "shingles",
      name: "Shingles (Zoster)",
      type: undefined,
      date: getVaccineDate("shingles_date") || getVaccineDate("zoster_date"),
      dataKey: "Shingles (Zoster)",
    },
    {
      key: "chickenpox",
      name: "Chickenpox (Varicella)",
      type: undefined,
      date:
        getVaccineDate("chickenpox_date") || getVaccineDate("varicella_date"),
      dataKey: "Chickenpox (Varicella)",
    },
    {
      key: "rsv",
      name: "RSV",
      type: undefined,
      date: getVaccineDate("rsv_date"),
      dataKey: "RSV",
    },
    {
      key: "cholera",
      name: "Cholera",
      type: undefined,
      date: getVaccineDate("cholera_date"),
      dataKey: "Cholera",
    },
    {
      key: "tick_borne_encephalitis",
      name: "Tick-Borne Encephalitis",
      type: undefined,
      date:
        getVaccineDate("tick_borne_encephalitis_date") ||
        getVaccineDate("tbe_date"),
      dataKey: "Tick-Borne Encephalitis",
    },
    {
      key: "pneumococcal",
      name: "Pneumococcal (Pneumonia)",
      type: undefined,
      date:
        getVaccineDate("pneumococcal_date") || getVaccineDate("pneumonia_date"),
      dataKey: "Pneumococcal (Pneumonia)",
    },
  ];

  const hasVaccines = vaccineList.some((vaccine) => {
    const status = vaccines[vaccine.key as keyof VaccineHistory];
    const isInReceivedArray = vaccinesReceived.some(
      (v: string) => v.toLowerCase() === vaccine.dataKey.toLowerCase()
    );
    const hasDate = !!vaccine.date;
    return (
      status === "yes" || status === "true" || isInReceivedArray || hasDate
    );
  });

  if (!hasVaccines && !vaccines.vaccine_records_available) {
    return null;
  }

  return (
    <div className="info-card vaccine-status">
      <h3>Vaccination Status</h3>
      <div
        className="vaccine-grid"
        style={{ display: "flex", flexDirection: "column", gap: "16px" }}
      >
        {vaccineList.map((vaccine, index) => {
          const status = vaccines[vaccine.key as keyof VaccineHistory];
          const isInReceivedArray = vaccinesReceived.some(
            (v: string) => v.toLowerCase() === vaccine.dataKey.toLowerCase()
          );
          const hasDate = !!vaccine.date;
          const isVaccinated =
            status === "yes" ||
            status === "true" ||
            isInReceivedArray ||
            hasDate;

          if (!isVaccinated) return null;

          return (
            <div
              key={vaccine.key}
              className="vaccine-item grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4 pb-3 border-b border-gray-200 items-start"
            >
              <span
                className="vaccine-name"
                style={{ fontWeight: "700", color: "#374151" }}
              >
                {vaccine.name}:
              </span>
              <span
                className={`vaccine-status ${isVaccinated ? "True" : "False"}`}
                style={{ color: "#1f2937" }}
              >
                {isVaccinated ? "Yes" : "No"}
                {vaccine.type && ` (${vaccine.type})`}
                {vaccine.date && (
                  <span
                    className="vaccine-date"
                    style={{
                      color: "#6b7280",
                      fontSize: "14px",
                      display: "block",
                      marginTop: "4px",
                    }}
                  >
                    Date: {formatDate(vaccine.date)}
                  </span>
                )}
              </span>
            </div>
          );
        })}

        {!hasVaccines && (
          <div
            style={{
              padding: "16px",
              textAlign: "center",
              color: "#6b7280",
              fontStyle: "italic",
            }}
          >
            No vaccines received
          </div>
        )}

        {(vaccines as any).vaccine_records_available !== undefined && (
          <div
            className="vaccine-item grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4 pt-3 border-t-2 border-gray-200 items-start"
          >
            <span
              className="vaccine-name"
              style={{ fontWeight: "700", color: "#374151" }}
            >
              Vaccine Records Available:
            </span>
            <span style={{ color: "#1f2937" }}>
              {(vaccines as any).vaccine_records_available === "true" ||
              (vaccines as any).vaccine_records_available === true
                ? "Yes"
                : "No"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
